import * as React from 'react';

import { rgba } from 'polished';
import styled from 'styled-components';

import moment from 'moment';

import { Slider, Rail, Handles } from 'react-compound-slider';

import Flex from '@components/layout/flex';
import PlayIcon from '@components/basics/icons/play';
import PauseIcon from '@components/basics/icons/pause';
import Handle from '@components/actions/slider/handle';
import Typography from '@components/basics/typography';

interface TrackSliderProps {
  className?: string;
  isPlaying: boolean;
  onTogglePlay: () => void;
  trackLength: number;
  startTime: number;
  maxTime: number;
  onChange: (startTime: number) => void;
}

interface TrackSliderState {
  values: number[];
  domain: number[];
  trackWidth: number;
}

export class TrackSlider extends React.Component<
  TrackSliderProps,
  TrackSliderState
> {
  constructor(props: TrackSliderProps) {
    super(props);

    const { startTime, maxTime } = this.props;

    this.state = {
      values: [startTime || 0],
      trackWidth: 0,
      domain: [0, maxTime],
    };
  }

  componentDidUpdate() {
    if (this.props.maxTime !== this.state.domain[1]) {
      this.setState({ domain: [0, this.props.maxTime] });
    }

    if (this.props.startTime !== this.state.values[0]) {
      this.setState({ values: [this.props.startTime || 0] });
    }
  }

  private onChange(newValues: any) {
    if (this) {
      if (this.state.values[0] !== newValues[0]) {
        this.props.onChange(newValues[0]);
      }
      this.setState({ values: newValues });
    }
  }

  private handleRail(el: HTMLDivElement | null) {
    if (el && this && this.state.trackWidth !== el.clientWidth) {
      this.setState({ trackWidth: el.clientWidth });
    }
  }

  private getStartTime() {
    return moment()
      .startOf('day')
      .seconds(this.props.startTime)
      .format('mm:ss');
  }

  private getSongLength() {
    return moment()
      .startOf('day')
      .seconds(this.props.trackLength)
      .format('mm:ss');
  }

  render() {
    const { values } = this.state;

    return (
      <div className={this.props.className}>
        <Flex alignItems="center" justifyContent="space-between">
          <Typography weight="700" type="small" opacity=".5">
            00:00
          </Typography>
          <Typography align="right" weight="700" type="small" opacity=".5">
            {this.getSongLength()}
          </Typography>
        </Flex>
        <Slider
          className="slider"
          mode={1}
          step={1}
          domain={this.state.domain}
          onChange={(values) => this.onChange(values)}
          values={values}
        >
          <Rail>
            {({ getRailProps }) => (
              <div
                ref={(el) => this.handleRail(el)}
                className="rail"
                {...getRailProps()}
              />
            )}
          </Rail>
          <Handles>
            {({ handles, getHandleProps }) => (
              <div className="slider-handles">
                {handles.map((handle) => (
                  <Handle
                    key={handle.id}
                    handle={handle}
                    domain={this.state.domain}
                    getHandleProps={getHandleProps}
                    trackWidth={this.state.trackWidth}
                    songLength={this.props.trackLength}
                  />
                ))}
              </div>
            )}
          </Handles>
        </Slider>
        <Flex alignItems="center" justifyContent="space-between">
          <Flex
            alignItems="center"
            className="play-wrapper"
            onClick={() => this.props.onTogglePlay()}
          >
            {this.props.isPlaying ? (
              <PauseIcon className="control-icon" />
            ) : (
              <PlayIcon className="control-icon" />
            )}
            <Typography weight="700" type="small">
              {this.props.isPlaying ? 'Pause' : 'Play'}
            </Typography>
          </Flex>
          <Typography weight="700" type="small">
            Starting at {this.getStartTime()}
          </Typography>
        </Flex>
      </div>
    );
  }
}

export default styled(TrackSlider)`
  position: relative;

  .slider {
    position: relative;
    height: 20px;
    width: 100%;
    display: flex;
    align-items: center;
    margin: 2px auto;
  }

  .rail {
    position: absolute;
    top: 50%;
    right: 0;
    left: 0;
    width: 100%;
    height: 4px;
    border-radius: 8px;
    background-color: ${({ theme }) => rgba(theme.colors.blueColor, 0.2)};
  }

  .play-wrapper {
    cursor: pointer;

    .control-icon {
      margin-right: 4px;
    }
  }
`;
